import React from "react";
import { cn } from "@/lib/utils";
import Button, { ButtonType } from "../reusable/Button";
import { LazyMotion, domAnimation, m } from "framer-motion";

interface ContactUsInformationProps {
  data: {
    sectionName?: string;
    topTitle: string;
    title: string;
    leftColumn: string;
    rightColumn: string;
    buttonText?: string;
    buttonLink: string;
  };
}

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function ContactUsInformation({
  data,
}: ContactUsInformationProps) {
  const sectionId = data.sectionName && data.sectionName.replace(/\s+/g, "-");

  return (
    <LazyMotion features={domAnimation}>
      <section
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      data-name={data.sectionName}
      className="w-full bg-accent-color-three-dark py-24"
    >
      <m.div
        className={cn("mx-auto flex flex-col gap-12", "max-w-[85vw]")}
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="flex flex-col gap-2 mb-8">
          <m.span
            className="font-sans text-special-h2 uppercase text-secondary tracking-widest"
            variants={fadeIn}
          >
            {data.topTitle}
          </m.span>
          <m.h2
            className="font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-accent-color-two-light"
            variants={fadeIn}
          >
            {data.title}
          </m.h2>
        </div>
        <div className="w-full flex flex-col desktop:flex-row gap-12">
          <m.div
            className="w-full desktop:w-1/2 flex flex-col gap-8"
            variants={fadeIn}
          >
            <div
              className="font-sans md:text-lg text-white text-paragraph-large [&_*:is(a)]:underline [&_*:is(ul)]:list-disc [&_*:is(ul)]:pl-6 [&_*:is(ol)]:list-decimal [&_*:is(ol)]:pl-6"
              dangerouslySetInnerHTML={{ __html: data.leftColumn }}
            />
          </m.div>
          <m.div
            className="w-full desktop:w-1/2 flex items-start"
            variants={fadeIn}
          >
            <div
              className="font-sans md:text-lg text-white text-paragraph-large [&_*:is(a)]:underline [&_*:is(ul)]:list-disc [&_*:is(ul)]:pl-6 [&_*:is(ol)]:list-decimal [&_*:is(ol)]:pl-6"
              dangerouslySetInnerHTML={{ __html: data.rightColumn }}
            />
          </m.div>
        </div>

        {data.buttonText && data.buttonLink && (
          <>
            <m.div
              className="flex desktop:hidden justify-center mt-8"
              variants={fadeIn}
            >
              <Button
                type={ButtonType.primary}
                title={data.buttonText.toUpperCase()}
                link={data.buttonLink}
                className="w-fit text-lg tracking-[0.15em]"
              />
            </m.div>
            <m.div className="hidden desktop:flex mt-4" variants={fadeIn}>
              <Button
                type={ButtonType.primary}
                title={data.buttonText.toUpperCase()}
                link={data.buttonLink}
                className="w-fit text-lg tracking-[0.15em]"
              />
            </m.div>
          </>
        )}
      </m.div>
    </section>
    </LazyMotion>
  );
}
